import _ from 'lodash';
import {
  OPTIONS,
  PACKAGES_AND_OPTIONS,
  PACKAGES,
  INTERIOR,
  EXTERIOR,
  WHEELS,
  SUMMARY,
} from '../../common/steps';

import normalizePackagesWithStructure, {
  nomrmalizeConflictPackageOptions,
} from './normalizePackages';
import { normalizeCGIForCards, normalizeCGIForCompare } from './normalizeCGI';
import { normalizeNetPriceDisplay } from './normalizeHelpers';
const every = _.every;
const get = _.get;

const getStepDataByName = (config, step) =>
  config.steps.find((x) => x.key === step);

function normalizeOptions(fullConfig) {
  const { config, modelMatrix } = fullConfig;
  const interior = _.flatMap(
    get(getStepDataByName(config, INTERIOR), 'categories'),
    'options'
  );
  const exterior = _.flatMap(
    get(getStepDataByName(config, EXTERIOR), 'categories'),
    'options'
  );
  const wheels = _.flatMap(
    get(getStepDataByName(config, WHEELS), 'categories'),
    'options'
  );
  const packages = _.flatMap(
    get(getStepDataByName(config, PACKAGES), 'categories', {}),
    'options'
  );
  const packagesAndOptions = _.flatMap(
    get(getStepDataByName(config, PACKAGES_AND_OPTIONS), 'categories', {}),
    'options'
  );

  const axelRatios = _.flatMap(modelMatrix.axleRatio);
  const engines = _.flatMap(modelMatrix.engine);
  const transmissions = _.flatMap(modelMatrix.transmission);

  const getOptions = () => {
    const optionsStep = getStepDataByName(config, OPTIONS);
    const optionsTabs = _.get(optionsStep, 'tabs');
    const hasOptionsTabs = optionsTabs?.length > 0;

    if (hasOptionsTabs) {
      return _.flatMap(_.flatMap(optionsTabs, 'categories'), 'options');
    }
    return _.flatMap(_.get(optionsStep, 'categories'), 'options');
  };

  return [].concat(
    axelRatios,
    engines,
    transmissions,
    packages,
    exterior,
    interior,
    wheels,
    getOptions(),
    packagesAndOptions
  );
}

function normalizePackages(packages) {
  //
  // This done is to make the lite data shaped like the normal packages data
  // description, disclaimer, id, large image url, small image url, video url

  if (packages.length && packages[0].descriptions) {
    packages.forEach((pkg) => {
      pkg.id = pkg.code;
      let descriptions = _.flatMap(pkg.descriptions);
      descriptions.forEach((description, id) => {
        pkg.packageOptions.push({
          description: description,
          id: id,
          disclaimer: '',
          largeImageUrl: '',
          smallImageUrl: '',
          videoUrl: '',
        });
      });
    });
  }

  _.each(packages, function (pkg) {
    pkg.numericMsrp = get(pkg, 'msrpDisplay.amount.value') || 0;
  });
  return packages;
}

function existingNormalizeFullConfig(data) {
  var selectedStyle = _.find(data.modelMatrix.styles, 'selected');

  data.modelMatrix.selectedStyleId = selectedStyle.id;

  //TODO: the following _each function is probably not needed.  Should be able to delete below, but want to wait until we stabalize UI to remove stuff
  _.each(data.modelMatrix.bodyTypes, function (config) {
    if (config.cabSize) {
      config.icon = 'cab_size_' + config.cabSize;
      config.iconClass = 'cab-size';
    } else if (config.wheelBaseType) {
      config.icon = 'wheel_base_' + config.wheelBaseType;
      config.iconClass = 'wheel-base';
    }
    if (config.icon) {
      config.icon = config.icon.replace(/_/g, '-').toLowerCase();
    }
  });

  const packageCategories = _.get(
    data.config.steps.find((step) => step.key === 'packages'),
    'categories',
    null
  );

  if (packageCategories) {
    data.config.packages = packageCategories.map((category) => {
      return { ...category, options: normalizePackages(category.options) };
    });
  } else {
    data.config.packages = [];
  }

  var optionsArray = normalizeOptions(data);

  // conflict check
  var add = data.config.toggleCheck.added;
  var del = data.config.toggleCheck.deleted;
  var choice = data.config.toggleCheck.userChoiceOptions;
  data.config.toggleCheck.userChoiceOptions = nomrmalizeConflictPackageOptions(
    data.config.toggleCheck.userChoiceOptions
  );

  data.hasConflict =
    (add && add.length > 0) ||
    (del && del.length > 0) ||
    (choice && choice.length > 0);

  //lots of services break when we don't remove certain RPO codes from this list.
  _.remove(data.config.selected, function (rpo) {
    return _.startsWith(rpo, '-');
  });

  data.options = optionsArray.reduce(function (hash, option) {
    // TODO:  Once v2 data is completely replaced, we should just use code
    const id = option.code || option.id;
    hash[id] = option;
    return hash;
  }, {});

  return data;
}

function findSelected(options) {
  return _.find(options, 'selected');
}

function findSelectedOrFirst(options) {
  return findSelected(options) || _.first(options);
}

function normalizeMsrp({ summary }) {
  const summaryExterior = summary.options.find(
    (option) => option.key === 'exterior'
  );
  const summaryWheels = summary.options.find(
    (option) => option.key === 'wheels'
  );
  const summaryInterior = summary.options.find(
    (option) => option.key === 'interior'
  );
  const summaryPackages = summary.options.find(
    (option) => option.key === 'packages'
  );
  const summaryOptions = summary.options.find(
    (option) => option.key === 'options'
  );
  const summaryPackagesAndOptions = summary.options.find(
    (option) => option.key === 'packages.and.options'
  );

  const netPriceData = get(summary, 'prices.netPrice');
  const isUnknownPrice = !!get(summary, 'prices.netPrice.unknown');
  return {
    isUnknownPrice,
    normalizedNetPriceDisplay: normalizeNetPriceDisplay(netPriceData),
    unknownPriceDisplay: netPriceData.unknown,
    isConfidencePrice: get(summary, 'prices.confidencePrice'),
    acTax: get(summary, 'prices.acTax'),
    luxuryTax: get(summary, 'prices.luxuryTax'),
    dealerFee: get(summary, 'prices.dealerFee'),
    accessoriesTotalValue: get(summary, 'accessories.price.amount.value'),
    accessoriesTotalText: get(summary, 'accessories.price.amount.text'),
    belowTheLineIncentives: get(summary, 'credits.netPrice.excluded') || [],
    opdTotal: get(summary, 'credits.opdTotal.amount.text'),
    opdTotalValue: get(summary, 'credits.opdTotal.amount.value'),

    totalIncludedIncentivesText: get(
      summary,
      'credits.netPrice.includedTotal.text'
    ),
    totalIncludedIncentivesValue: get(
      summary,
      'credits.netPrice.includedTotal.value'
    ),
    totalMsrpWithOptions: get(summary, 'prices.vehicleOptionsTotal.value'),
    totalMsrpBeforeOpd: get(summary, 'prices.vehicleTotalBeforeOpd.text'),
    totalMsrp: get(summary, 'prices.totalVehiclePrice.value'),
    msrpForResidualCalculation:
      get(summary, 'prices.msrpForResidualCalculation.value') || 0,
    vehicleTotalEmployeePriceForPe:
      get(summary, 'prices.vehicleTotalEmpPriceForPe.value') || 0,
    supplierPrice: get(summary, 'prices.supplierPrice.value'),
    employeePrice: get(summary, 'prices.employeePrice.value') || 0,
    standardVehiclePrice: get(summary, 'prices.standardVehiclePrice'),
    destination: get(summary, 'prices.destination'),
    gasGuzzlerTax: get(summary, 'prices.gasGuzzlerTax'),
    miscFees: get(summary, 'prices.miscFees'),
    totalVehiclePrice: get(summary, 'prices.totalVehiclePrice'),
    accessoriesTotal:
      get(summary, 'accessories.price.amount') ||
      get(summary, 'accessories.price.display') ||
      0,
    exteriorOptionsTotal:
      get(summaryExterior, 'price.amount.text') ||
      get(summaryExterior, 'price.display'),
    wheelOptionsTotal:
      get(summaryWheels, 'price.amount.text') ||
      get(summaryWheels, 'price.display'),
    interiorOptionsTotal:
      get(summaryInterior, 'price.amount.text') ||
      get(summaryInterior, 'price.display'),
    packagesOptionsTotal: {
      text:
        get(summaryPackages, 'price.amount.text') ||
        get(summaryPackages, 'price.display'),
      value: get(summaryPackages, 'price.amount.value'),
    },
    optionsTotal: {
      text:
        get(summaryOptions, 'price.amount.text') ||
        get(summaryOptions, 'price.display'),
      value: get(summaryOptions, 'price.amount.value'),
    },
    packagesAndOptionsTotal:
      get(summaryPackagesAndOptions, 'price.amount.text') ||
      get(summaryPackagesAndOptions, 'price.display'),
  };
}

const normalizedEligiblePrice = (eligiblePrice, assetsBrand) => {
  if (typeof eligiblePrice === 'undefined' || assetsBrand !== 'ff') {
    return true;
  }
  return eligiblePrice;
};

const computeModelEligibility = (styles) =>
  every(styles, { eligibleForInvoicePrice: false });

const getLegalDisclosure = (styleId, modelMatrix) => {
  const { styles } = modelMatrix;
  const selectedStyle = _.find(styles, { styleId: styleId });
  return get(selectedStyle, 'vehicleInfo.cgiDisclaimer', '');
};

function normalizeFullConfig(fullConfig, modelInfo) {
  let { modelMatrix, config } = fullConfig;
  const { assetsBrand } = modelInfo;

  let styles = modelMatrix.styles.map((style) => {
    return _.assign({}, style, {
      mmc: style.vehicleInfo.code.replace(/_.*/, ''),
      peg: _.head(style.vehicleInfo.cgiCode.split('-')),
      defaultExteriorColor: style.vehicleInfo.defaultColor,
      eligibleForInvoicePrice: normalizedEligiblePrice(
        style.eligibleForInvoicePrice,
        assetsBrand
      ),
    });
  });

  var style = findSelected(styles);

  var driveType =
    findSelected(modelMatrix.driveTypes) ||
    findSelected(modelMatrix.motorizations);
  var bodyType = findSelectedOrFirst(modelMatrix.bodyTypes);
  var engine = findSelected(modelMatrix.engines);
  var axleRatio = findSelected(modelMatrix.axleRatios);
  var transmission = findSelected(modelMatrix.transmissions);
  var motorization = findSelected(modelMatrix.motorizations);

  var interiorColor = findSelectedOrFirst(
    _.flatMap(
      getStepDataByName(config, 'interior').categories.filter(
        (c) => c.key === 'interior.color'
      ),
      'options'
    )
  );
  var exteriorColor = findSelectedOrFirst(
    _.flatMap(
      getStepDataByName(config, 'exterior').categories.filter(
        (c) => c.key === 'exterior.color'
      ),
      'options'
    )
  );

  var styleId = _.get(style, 'styleId');

  const normalizeSelectionsRearWheelType = (type) => {
    //TODO: If backend eventually returns null for x.rearWheelType, we can delete the above function normalizeSelectionsRearWheelType().  Yes, this is gross.
    if (type === null) {
      return '';
    }
    return type;
  };

  var selections = {
    styleId: styleId,
    trimName: _.get(style, 'trimName', ''),
    driveType: _.get(driveType, 'id'),
    rearWheelType: normalizeSelectionsRearWheelType(
      _.get(bodyType, 'rearWheelType', '')
    ),
    motorization: _.get(motorization, 'id'),
    bodyTypeId: _.get(bodyType, 'id'),
    bodyType: _.get(bodyType, 'id', ''),
    engine: _.get(engine, 'code'),
    cabSize: _.get(bodyType, 'cabSize', ''),
    axleRatio: _.get(axleRatio, 'code'),
    transmission: _.get(transmission, 'code'),
    rpos: _.get(config, 'selected'),
    accessories: _.get(config, 'VEHICLE.SELECTED_ACCESSORY_LIST'),
    interiorColor: _.get(interiorColor, 'code'),
    exteriorColor: _.get(exteriorColor, 'code'),
    interiorColorDescription: _.get(interiorColor, 'description'),
    exteriorColorDescription: _.get(exteriorColor, 'description'),
    transmissionDescription: _.get(transmission, 'description'),
    engineDescription: _.get(engine, 'description'),
  };

  const msrp = normalizeMsrp(config);

  const normalizedPackages = normalizePackagesWithStructure(config.packages);

  var incentives = {
    deadline: style.vehicleInfo.nationalIncentiveEndDate,
    value: _.get(style, 'vehicleInfo.nationalIncentive.value', 0),
  };

  const transmissions = modelMatrix.transmissions || [];

  const stylesWithCGI = styles.map((style) => {
    return {
      ...style,
      cgi: normalizeCGIForCards(
        modelInfo,
        style.vehicleInfo,
        modelInfo.cgiData.trimCardDefaultAngle,
        modelInfo.cgiData.cgiSizes.small
      ),
      cgiTransparent: normalizeCGIForCompare(
        modelInfo,
        style.vehicleInfo,
        modelInfo.cgiData.trimCardDefaultAngle,
        modelInfo.cgiData.cgiSizes.small
      ),
    };
  });

  const flattenedProps = {
    msrp: msrp,
    incentives: incentives,
    styles: stylesWithCGI,
    style: style,
    styleId: styleId,
    eligibleForInvoicePrice: normalizedEligiblePrice(
      get(config, 'summary.prices.eligibleForInvoicePrice'),
      assetsBrand
    ),
    isModelIneligible: computeModelEligibility(modelMatrix.styles),
    engines: modelMatrix.engines,
    engine: engine,
    bodyTypes: modelMatrix.bodyTypes,
    bodyType: bodyType,
    driveTypes: modelMatrix.driveTypes,
    driveType: driveType,
    motorizations: modelMatrix.motorizations || [],
    motorization: motorization,
    transmissions,
    transmission: transmission,
    axleRatios: modelMatrix.axleRatios,
    axleRatio: axleRatio,
    options: fullConfig.options,
    packages: normalizedPackages,
    packagesAndOptions: getStepDataByName(config, 'packages.and.options'),
    interior: getStepDataByName(config, INTERIOR),
    exterior: getStepDataByName(config, EXTERIOR),
    wheels: getStepDataByName(config, WHEELS),
    summary: get(config, SUMMARY),
    exteriorInteriorOptions: getStepDataByName(config, OPTIONS),
    technicalSpecifications: config.technicalSpecifications,
    ss: config.ss,
    selections: selections,
    mmc: style.mmc,
    peg: style.peg,
    legalDisclosure: getLegalDisclosure(styleId, modelMatrix),
  };

  return _.assign(fullConfig, flattenedProps);
}

export default (modelInfo) => (data) =>
  normalizeFullConfig(existingNormalizeFullConfig(data), modelInfo);
